// deps
import styled, { keyframes, css } from "styled-components";

// internal deps
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { CrossSellM2ItemConfig } from "./config";

export const Container = styled.div<{ isPopCart?: boolean; hasFlipText?: boolean; isSnaccStorefrontV2?: boolean }>(
    ({ theme, isPopCart, hasFlipText, isSnaccStorefrontV2 }) => {
        const { width, height, margin } = CrossSellM2ItemConfig(theme, isPopCart, isSnaccStorefrontV2).container;

        const border = "none";
        const flexDirection = "column-reverse";
        const background = "transparent";

        return {
            border,
            width,
            height: `${parseInt(height) + (hasFlipText ? 15 : 0)}px`,
            borderRadius: `${theme.cornerRadius.small}`,
            display: "flex",
            flexDirection,
            position: "relative",
            justifyContent: "space-between",
            background,
            margin,
        };
    },
);

export const FoodIconContainer = styled.span(({ theme }) => ({
    width: "fit-content",
    height: "fit-content",
    margin: `1px ${theme.spacing.xxSmall} 0px 0px`,
    verticalAlign: "middle",
}));

export const Details = styled.div(({ theme }) => ({
    display: "flex",
    justifyContent: "flex-start",
    width: CrossSellM2ItemConfig(theme).textContainerWidth,
}));

export const TextContainer = styled.div<{ isSnaccStorefrontV2?: boolean }>(({ theme, isSnaccStorefrontV2 }) => ({
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    padding: CrossSellM2ItemConfig(theme).textContainerPadding,
    paddingRight: theme.spacing.xSmall,
    ...(isSnaccStorefrontV2 && {
        paddingTop: theme.spacing.xxSmall,
    }),
    width: CrossSellM2ItemConfig(theme).textContainerWidth,
}));

export const AddButtonContainer = styled.div(({ theme }) => {
    return {
        position: "absolute",
        right: `${CrossSellM2ItemConfig(theme).addItemContainer.right}`,
        top: `${CrossSellM2ItemConfig(theme).addItemContainer.top}`,

        "&:after": {
            content: "",
            position: "absolute",
            top: "-4px",
            right: "-6px",
            bottom: "-4px",
            left: "-6px",
        },
    };
});

export const ImageContainer = styled.div<{ isPopCart?: boolean; isSnaccStorefrontV2?: boolean }>(
    ({ theme, isPopCart, isSnaccStorefrontV2 }) => {
        const { width, height } = CrossSellM2ItemConfig(theme, isPopCart, isSnaccStorefrontV2).imageContainer;

        const borderRadius = isPopCart ? theme.spacing.small : theme.spacing.xSmall;
        const border = `1px solid ${theme.colors[theme.colorMode].border_Secondary}`;

        return {
            borderRadius: isPopCart ? theme.spacing.small : theme.spacing.xSmall,
            overflow: "hidden",
            width,
            height,
            margin: "auto",
            flexShrink: 0,
            minHeight: height,
            "> img": {
                width: "100%",
                height: "calc(100% - 2px)",
                "object-fit": "cover",
                borderRadius,
                border,
                ...(isSnaccStorefrontV2 && {
                    transform: "scale(1.6)",
                    "transform-origin": "center",
                }),
            },
        };
    },
);

export const Name = styled(Text)<{ isPopCart?: boolean }>`
    overflow: hidden;
    text-overflow: ellipsis;
    width: ${(props) => CrossSellM2ItemConfig(props.theme, props.isPopCart).name.width};
    height: 32px;
    margin-bottom: ${(props) => props.theme.spacing.xxSmall};
    line-height: 15px;
`;

export const BasePriceContainer = styled(Text)`
    text-decoration: line-through;
    text-decoration-line: line-through;

    margin-right: ${(props) => props.theme.spacing.xxSmall};
`;

export const FlexContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
`;

export const LockIconContainer = styled.div(({ theme }) => ({
    position: "absolute",
    right: `${CrossSellM2ItemConfig(theme).addItemContainer.right}`,
    top: `${CrossSellM2ItemConfig(theme).addItemContainer.top}`,
    width: `calc(${theme.spacing.large} + 4px)`,
    height: `calc(${theme.spacing.large} + 4px)`,
    borderRadius: `calc(${theme.spacing.xSmall} + 0.5px)`,
    border: `1px solid ${theme.colors[theme.colorMode].border_Secondary}`,
    backgroundColor: theme.colors[theme.colorMode].background_Primary,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    "&:after": {
        content: "",
        position: "absolute",
        top: "-4px",
        right: "-6px",
        bottom: "-4px",
        left: "-6px",
    },
}));
export const SurpriseContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 100%;
    margin-bottom: ${(props) => props.theme.spacing.xxxSmall};
`;

export const TickerWrapper = styled.div`
    position: relative;
    width: ${(props) => `calc(${props.theme.spacing.xLarge} * 2 + ${props.theme.spacing.xxxSmall} + 1px)`};
    height: ${(props) => `calc(${props.theme.spacing.medium} - 2px)`};
    flex-shrink: 0;
    margin-right: ${(props) => props.theme.spacing.xxSmall};
    overflow: hidden;
`;

const scrollDown = keyframes`
    0% {
        transform: translateY(0);
    }
    20% {
        transform: translateY(0);
    }
    25% {
        transform: translateY(-14px);
    }
    45% {
        transform: translateY(-14px);
    }
    50% {
        transform: translateY(-28px);
    }
    70% {
        transform: translateY(-28px);
    }
    75% {
        transform: translateY(-42px);
    }
    95% {
        transform: translateY(-42px);
    }
    100% {
        transform: translateY(-56px);
    }
`;

export const TickerContent = styled.div<{ shouldAnimate?: boolean; animationDelay?: string }>`
    display: flex;
    flex-direction: column;
    ${({ shouldAnimate, animationDelay }) =>
        shouldAnimate &&
        css`
            animation: ${scrollDown} 6s linear infinite;
            animation-delay: ${animationDelay || "0ms"};
        `}
`;

export const TickerItem = styled(Text)`
    line-height: ${(props) => `calc(${props.theme.spacing.medium} )`};

    height: ${(props) => `calc(${props.theme.spacing.medium} - 2px)`};
    display: flex;
    align-items: center;
`;
